import { Button } from '@/app/common/components/button/Button'
import DefaultAvatar from '@/app/pages/profile/components/DefaultAvatar'
import { cn, currencyFormatter } from '@/lib/utils'
import { Badge } from '@/shad/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { PopoverContent, PopoverTrigger } from '@/shad/ui/popover'
import { Popover } from '@radix-ui/react-popover'
import { AlertTriangle, EllipsisVertical } from 'lucide-react'
import { useState } from 'react'
import { StudentAwardInformation } from '../../award-form'

interface StudentCardProps {
  student: StudentAwardInformation
  isManuallyAwarded: boolean
  openPopoverId: number | null
  setOpenPopoverId: (id: number | null) => void
  onManualAwardClick: (studentId: number) => void
  onRemoveManualAward: (studentId: number) => void
}

export function StudentCard({
  student,
  isManuallyAwarded,
  openPopoverId,
  setOpenPopoverId,
  onManualAwardClick,
  onRemoveManualAward,
}: StudentCardProps) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const handleRemoveClick = () => {
    setConfirmDialogOpen(true)
    setOpenPopoverId(null)
  }

  const handleConfirmRemove = () => {
    if (student.id !== null) {
      onRemoveManualAward(student.id)
      setConfirmDialogOpen(false)
    }
  }

  const handleCancelRemove = () => {
    setConfirmDialogOpen(false)
  }

  return (
    <>
      <div className="group relative py-6 border-gray-100 last:border-b-0 transition-colors">
        {/* Header with Avatar, Name, and Options */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary/10 border border-primary/20 rounded-full">
                <DefaultAvatar
                  variant={'neutral'}
                  firstName={student.first_name}
                  lastName={student.last_name}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 text-sm">
                  {student.first_name} {student.last_name} - {student.id}
                </h3>
                {isManuallyAwarded && (
                  <Badge variant={'yellow'}>Manually Awarded</Badge>
                )}
              </div>
              <div className="flex items-center gap-1 text-base text-gray-800">
                <span className="font-normal text-sm text-gray-600">
                  {student.applciant_email}
                </span>
              </div>
            </div>
          </div>

          {/* Options Menu */}
          <Popover
            open={openPopoverId === student.id}
            onOpenChange={(open) => setOpenPopoverId(open ? student.id : null)}
          >
            <PopoverTrigger asChild>
              <Button size={'sm'} variant={'outline'} className="rounded-2xl">
                Options
                <EllipsisVertical className="h-4 w-4 text-gray-400 ml-2" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="end">
              <div className="space-y-1">
                <button
                  onClick={() => {
                    if (student.id !== null) {
                      onManualAwardClick(student.id)
                    }
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded-md transition-colors"
                >
                  <div className="font-medium text-gray-900">
                    Change award amount
                  </div>
                  <div className="text-xs text-gray-600">
                    Manually set the amount a student should receive
                  </div>
                </button>
                <button
                  onClick={handleRemoveClick}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded-md transition-colors"
                >
                  <div className="font-medium text-gray-900">
                    Remove manual award
                  </div>
                  <div className="text-xs text-gray-600">
                    Remove the override amount
                  </div>
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Award Amounts - Vertical Layout */}
        <div className="mt-2">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 mb-1">Base Award</div>
            <div
              className={cn(
                'font-semibold text-sm text-gray-900',
                student.isManuallyAwarded && 'line-through'
              )}
            >
              {currencyFormatter.format(student.base_award_amount)}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 mb-1">
              Additional Modifier Award
            </div>
            <div
              className={cn(
                'font-semibold text-sm text-gray-900',
                student.isManuallyAwarded && 'line-through'
              )}
            >
              {currencyFormatter.format(student.modifier_total)}
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
            <div className="text-base font-medium text-gray-800 mb-1">
              Total Award
            </div>
            <div className="font-bold tracking-tight text-gray-800 text-base">
              {currencyFormatter.format(student.amount_to_be_awarded)}
            </div>
          </div>
        </div>
      </div>

      {/* Remove Manual Award Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
              <DialogTitle className="text-left">
                Remove Manual Award
              </DialogTitle>
            </div>
            <DialogDescription className="text-left">
              Are you sure you want to remove the manual award for this student?
              This will revert them back to the standard award amount based on
              awarding method configured for the program purse.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <Button
              variant="outline"
              onClick={handleCancelRemove}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRemove}
              className="w-full sm:w-auto"
            >
              Remove Award
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
